import React from 'react'
import clsx from 'clsx'
import { useHistory, useParams } from 'react-router'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { More, UserAdd } from 'iconsax-react'
import { useTheme } from '@mui/material'

import MediaAvatar from '../../../atoms/MediaAvatar'
import Spacer from '../../../atoms/Spacer'
import ButtonMenu from '../../../molecules/ButtonMenu'
import SaveButton from '../../../atoms/Button2'
import MergeContactModal from '../../../molecules/MergeContactModal'
import FavoriteButton from '@SonarCX/components/atoms/FavoriteButton'
import PinButton from '@SonarCX/components/atoms/PinButton'

import { MAX_FILESIZE } from '@SonarCX/utilities/constants'
import { Routes } from '@SonarCX/Routes'
import { createViewableSession } from '../../../molecules/BaseConversation/RecentActivity/helper'
import { useCustomerContext } from '@SonarCX/providers/CustomerProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { useEditContext } from '@SonarCX/providers/EditProvider'
import {
  useSocket,
  CONVERSATION_FILTERS
} from '@SonarCX/providers/SocketProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { removeMemberFromTeams } from '@SonarCX/slices/teamsSlice'
import useFavorites from '@SonarCX/hooks/useFavorites'

import useStyles from './styles'

const SAVE = 'Save'
const DELETE = 'Delete'
const SAVE_AND_CLOSE = 'Save & Close'
const GO_TO_TIMELINE = 'Go To Timeline'
const MERGE_CONTACT = 'Merge'

const DetailsTopBar = ({
  user,
  img,
  isTeam,
  canEdit,
  canDelete,
  setImg,
  customerVersion,
  imageHasBeenEdited,
  setImageHasBeenEdited
}) => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const { favoriteUsers } = useFavorites()
  const {
    values,
    handleUpdateUser,
    customerSession,
    resetVals,
    isSaving: isAPISaving,
    savedMessage: successfulAPISave,
    setSavedMessage,
    onFiles,
    setOnFiles
  } = useCustomerContext()
  const { deleteUsers } = useUserAPI()
  const {
    editedMeta,
    setEditedMeta,
    editedAuth,
    editedImg,
    setEditedImg,
    forceSave,
    setForceSave
  } = useEditContext()
  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const {
    api: { handleFilterChange }
  } = useSocket()
  const { isSmall, isExtraSmall, down768 } = useBreakpoint()
  const theme = useTheme()
  const [saving, setSaving] = React.useState(false)
  const menuRef = React.useRef()
  const [url, setUrl] = React.useState(null)
  const [disable, setDisable] = React.useState(false)
  const [merge, setMerge] = React.useState(false)

  const classes = useStyles()
  const options = React.useMemo(() => {
    const opts = []

    if (isSmall) {
      opts.push(SAVE_AND_CLOSE)

      if (!isTeam) {
        opts.push(GO_TO_TIMELINE)
      }
    }

    if (canEdit && !customerVersion) {
      opts.push(SAVE)
    }

    if (canDelete) {
      opts.push(DELETE)
    }

    if (isSmall) {
      let reordered = []
      if (!isTeam) {
        reordered.push(SAVE_AND_CLOSE)
        reordered.push(DELETE)
        reordered.push(GO_TO_TIMELINE)
      } else {
        reordered = [opts[1], opts[0], opts[2]]
      }
      if (canEdit && !isTeam) {
        reordered.push(MERGE_CONTACT)
      }
      return reordered
    }

    if (canEdit && !isTeam) {
      opts.push(MERGE_CONTACT)
    }

    return opts
  }, [isTeam, canEdit, canDelete, isSmall, customerVersion])

  const isFavorite = React.useMemo(() => {
    if (!user.id) return false

    return favoriteUsers.some(fav => fav.item_id === user.id)
  }, [favoriteUsers, user])

  const handleUploadReturn = async () => {
    try {
      setSaving(true)
      const success = await handleUpdateUser()

      if (success) {
        const location = history.location.pathname.split('/')
        const newLocation = location.slice(0, location.length - 1).join('/')

        // Gives a chance for the save notification to show
        setTimeout(() => history.push(newLocation), 500)
      }
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    } finally {
      setSaving(false)
    }
  }

  const handleUpload = async () => {
    try {
      setSaving(true)
      setForceSave(false)
      await handleUpdateUser()
      setEditedMeta(false)
      setDisable(true)
      dispatch(AddSuccessAlert('Changes saved'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    } finally {
      setSaving(false)
    }
  }

  const deleteClient = () => {
    setTitle('ARE YOU SURE?')
    setReason(`Are you sure you want to delete '${user?.name}'?`)
    setSubtext('')
    setV2(true)
    setAction(() => {
      deleteUsers([user.id]).then(async () => {
        //delete user from any teams
        if (isTeam) {
          await dispatch(removeMemberFromTeams(user.id))
        }
        dispatch(AddSuccessAlert('Changes saved'))
        history.goBack()
      })
    })
  }

  const goToTimeline = async () => {
    if (customerSession) {
      handleFilterChange(CONVERSATION_FILTERS.OPEN, false)
      history.push(`${Routes.TIMELINE}/?session=${customerSession}`)
    } else {
      // no recent activities on the user
      const newSessionID = await createViewableSession(id)
      history.push(`${Routes.TIMELINE}/?session=${newSessionID}`)
    }
  }

  const handleSelect = opt => {
    if (opt === SAVE) {
      handleUpload()
    } else if (opt === DELETE) {
      deleteClient()
    } else if (opt === SAVE_AND_CLOSE) {
      handleUploadReturn()
    } else if (opt === GO_TO_TIMELINE) {
      goToTimeline()
    } else if (opt === MERGE_CONTACT) {
      setMerge(true)
    }
  }

  React.useEffect(() => {
    if (img) {
      setUrl(URL.createObjectURL(img))
    } else {
      setUrl(null)
    }
  }, [img])

  React.useEffect(() => {
    if (isTeam) {
      if (forceSave) {
        setDisable(false)
        return
      }

      if (!editedMeta && !editedAuth && !editedImg) {
        setDisable(true)
      } else {
        setDisable(false)
      }
    } else {
      // no need to check for auth for customers
      if (!editedMeta && !editedImg) {
        setDisable(true)
      } else {
        setDisable(false)
        setSavedMessage(false)
      }
    }
  }, [editedMeta, isTeam, editedAuth, editedImg, setSavedMessage, forceSave])

  React.useEffect(() => {
    if (imageHasBeenEdited) {
      handleUpdateUser()
      setImageHasBeenEdited(false)
      setDisable(true)
      setEditedImg(false)
    }
  }, [
    imageHasBeenEdited,
    setImageHasBeenEdited,
    handleUpdateUser,
    setDisable,
    setEditedImg,
    values
  ])

  const handleImgUpload = async e => {
    let file
    if (e.target.files.length !== 0) {
      file = e.target.files[0]

      // checking file size before uploading
      if (file.size > MAX_FILESIZE) {
        dispatch(
          AddErrorAlert({
            err: {
              message: `${file.name} is too large -- files must be smaller than 100MB.`
            }
          })
        )
      } else {
        // upload image to avatar area
        setImg(file)
      }
    }
  }

  const handleProfileTabClick = e => {
    e.preventDefault()
    e.stopPropagation()
    setOnFiles(false)
  }

  const getButtonStatus = () => {
    return successfulAPISave ? 'SAVED' : 'SAVE'
  }

  return (
    <>
      <div className={classes.root}>
        <div className={classes.tabs}>
          <div className={clsx(classes.tab, classes.avatarTab)}>
            <label>
              {isSmall && (
                <input
                  type='file'
                  hidden
                  onChange={e => handleImgUpload(e)}
                  disabled={!canEdit}
                />
              )}

              {url ? (
                <div
                  className={classes.avatar}
                  style={{
                    position: 'relative',
                    backgroundImage: `url(${url})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    borderRadius: '100%'
                  }}
                />
              ) : (
                <MediaAvatar user={user} hideCard className={classes.avatar} />
              )}

              <div
                className={clsx(
                  classes.tabName,
                  !onFiles && classes.selectedTab,
                  classes.name
                )}
                onClick={handleProfileTabClick}
              >
                {isTeam ? user?.name ?? 'User' : user?.name ?? 'Contact'}
                {down768 && user?.deleted_at && <div>(Deleted)</div>}
              </div>
            </label>

            <div
              className={clsx(
                classes.tabBottom,
                !onFiles && classes.tabBottomActive
              )}
            />
          </div>

          <div onClick={() => setOnFiles(true)} className={classes.tab}>
            <label
              className={clsx(classes.tabName, onFiles && classes.selectedTab)}
            >
              Files
            </label>

            <div
              className={clsx(
                classes.tabBottom,
                onFiles && classes.tabBottomActive
              )}
            />
          </div>
        </div>

        <Spacer />

        {canEdit && !isExtraSmall && (
          <SaveButton
            onClick={handleUpload}
            className={classes.save}
            loading={saving || isAPISaving}
            endIcon={<UserAdd size={theme.size.sm} />}
            loadingPosition='end'
            disabled={disable}
          >
            {customerVersion ? getButtonStatus() : 'SAVE'}
          </SaveButton>
        )}

        {options.length > 0 && (
          <div ref={menuRef} className={classes.menu}>
            <ButtonMenu
              testid='contact-options-menu'
              id='options'
              options={options}
              onSelect={handleSelect}
              origin={{
                anchor: { vertical: 'bottom', horizontal: 'right' },
                transform: { vertical: 'top', horizontal: 'right' }
              }}
              targetRef={menuRef}
            >
              <More className={classes.menuIcon} />
            </ButtonMenu>
          </div>
        )}

        <FavoriteButton itemId={user.id} isFavorite={isFavorite} type='user' />

        <PinButton
          id={user.id}
          type={isTeam ? 'team-member' : 'contact'}
          title={user.name}
          className={classes.pinButton}
        />
      </div>

      {merge && (
        <MergeContactModal
          currentUser={user}
          session={customerSession}
          onClose={() => setMerge(false)}
          onConfirm={val => {
            resetVals()
            history.replace(`${Routes.CUSTOMERS}/${val.id}`)
          }}
        />
      )}
    </>
  )
}

export default DetailsTopBar
